/**
 * Vocabulary and formatting carried over from the Qwik platform.
 *
 * The legacy stylesheet keys colours off human labels (`[data-status='Out On
 * Proof']`), and the UI copy uses the Vendure-era ids (`addressedMail`,
 * `regularMail`). This module is the single translation table between those
 * and the snake_case values the API stores.
 */

import { isStaff } from "@shared/staff.ts";
import type { Artwork, Campaign, Customer, Order, Product } from "./api.ts";

export type LegacyStatus =
  | "In Progress"
  | "Out On Proof"
  | "Complete"
  | "Posted"
  | "Cancelled"
  | "Rejected"
  | "Deleted";

export const STATUS_TO_LABEL: Record<string, LegacyStatus> = {
  in_progress: "In Progress",
  out_on_proof: "Out On Proof",
  complete: "Complete",
  posted: "Posted",
  cancelled: "Cancelled",
  rejected: "Rejected",
  deleted: "Deleted",
};

export const STATUS_ORDER: LegacyStatus[] = [
  "In Progress",
  "Out On Proof",
  "Complete",
  "Posted",
  "Cancelled",
  "Rejected",
  "Deleted",
];

export const APPROVAL_TO_LABEL: Record<string, string> = {
  pending: "Pending",
  approved_by_creator: "Approved by Creator",
  rejected: "Rejected",
  approved_for_print: "Approved for Print",
};

export const statusLabel = (c: Pick<Campaign, "status">): LegacyStatus =>
  STATUS_TO_LABEL[c.status] ?? "In Progress";

/**
 * A Complete campaign whose lodgement date has passed is shown as Posted,
 * which is how the legacy list and orders page classified it.
 */
/** Artwork the customer still has to approve, or that was sent back. */
export function artworkNeedsApproval(
  c: Pick<Campaign, "status" | "artwork_id" | "design_id" | "approval_status">,
): boolean {
  return (
    c.status !== "deleted" &&
    Boolean(c.artwork_id || c.design_id) &&
    (c.approval_status === "pending" || c.approval_status === "rejected")
  );
}

export function effectiveStatus(c: Campaign): LegacyStatus {
  const label = statusLabel(c);
  if (label === "Complete" && c.lodgement_date && c.lodgement_date * 1000 < startOfToday())
    return "Posted";
  return label;
}

/**
 * The status a row should show, plus the copy the pill reads. Staff see a
 * creator-approved campaign as Out On Proof ("Approve me") because that is
 * the queue they act on; customers see Complete as "Approved".
 */
const PAID_AWAITING = "Paid – awaiting artwork / print approval";

export function campaignDisplayStatus(
  c: Campaign,
  adminMode: boolean,
): { status: LegacyStatus; label: string } {
  const paidAwaiting =
    adminMode &&
    c.status !== "deleted" &&
    c.status !== "cancelled" &&
    c.status !== "posted" &&
    (c.order_status === "paid" || c.order_status === "in_production") &&
    c.approval_status !== "approved_for_print";
  if (paidAwaiting) {
    return { status: effectiveStatus(c), label: PAID_AWAITING };
  }
  const status: LegacyStatus =
    adminMode && c.approval_status === "approved_by_creator" && c.status !== "deleted"
      ? "Out On Proof"
      : effectiveStatus(c);
  let label: string = status;
  if (status === "Complete") label = "Approved";
  if (adminMode && status === "Out On Proof") label = "Approve me";
  return { status, label };
}

export const startOfToday = (): number => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export const MAIL_SPEED_OPTIONS = [
  { id: "regular", name: "Regular Mail" },
  { id: "priority", name: "Priority Mail" },
];

export const MAP_SELECTION_MODE_OPTIONS = [
  { id: "radius", name: "Radius Selection" },
  { id: "polygon", name: "Custom Draw Polygons" },
  { id: "suburb", name: "Suburbs" },
  { id: "postcode", name: "Postcodes" },
  { id: "lga", name: "Local Government Areas" },
  { id: "electoral", name: "Electoral Zones" },
];

export { UNADDRESSED_SELECTIONS } from "@shared/mail-rules.ts";

export const STATE_OR_TERRITORY_OPTIONS = [
  { id: "Australia", name: "Australia (all zones)" },
  { id: "VIC", name: "Victoria" },
  { id: "NSW", name: "New South Wales" },
  { id: "QLD", name: "Queensland" },
  { id: "SA", name: "South Australia" },
  { id: "ACT", name: "Australian Capital Territory" },
  { id: "TAS", name: "Tasmania" },
  { id: "WA", name: "Western Australia" },
  { id: "NT", name: "Northern Territory" },
];

/** Five days, as in the legacy createCampaign / duplicate flows. */
export { LODGEMENT_BUFFER_DAYS, SUPPORT_PHONE } from "@shared/campaign-rules.ts";

// ------------------------------------------------------------ formatting

const toDate = (v: number | string | null | undefined): Date | null => {
  if (v === null || v === undefined || v === "") return null;
  return typeof v === "number" ? new Date(v * 1000) : new Date(v);
};

/** `dd MMM yy`, or TBA. Same as the legacy `formatDate`. */
export function formatDate(v: number | string | null | undefined): string {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "TBA";
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" });
}

/** `h:mm am`, the legacy `formatTime`. */
export function formatTime(v: number | string | null | undefined): string {
  const d = toDate(v);
  if (!d || Number.isNaN(d.getTime())) return "";
  const hours = d.getHours();
  const h = hours % 12 === 0 ? 12 : hours % 12;
  return `${h}:${d.getMinutes().toString().padStart(2, "0")} ${hours >= 12 ? "pm" : "am"}`;
}

/**
 * The next "Zone N" label. Uses the highest existing number, not the count,
 * so deleting Zone 2 and drawing again does not produce a second Zone 3.
 */
export function nextZoneName(zones: { label: string | null }[]): string {
  const max = zones.reduce((highest, zone) => {
    const match = zone.label?.match(/zone (\d+)/i);
    return Math.max(highest, match ? Number.parseInt(match[1] ?? "0", 10) : 0);
  }, 0);
  return `Zone ${max + 1}`;
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Lodgement day, or today, as `28 Sep`. UTC, matching the date field. */
function shortCampaignDate(epochSeconds: number | null): string {
  const d = new Date((epochSeconds ?? Math.floor(Date.now() / 1000)) * 1000);
  return `${d.getUTCDate()} ${SHORT_MONTHS[d.getUTCMonth()]}`;
}

/**
 * Suggested name for a campaign that is still called "New Campaign".
 * A taken base gets ` 2`, then ` 3`. The first one has no number.
 */
export function autoCampaignName(opts: {
  productName?: string | null;
  mailType: "addressed" | "unaddressed";
  mailSpeed: "regular" | "priority";
  lodgementDate: number | null;
  taken: string[];
}): string {
  const base = [
    "Campaign",
    opts.productName?.trim(),
    opts.mailType === "addressed" ? "Add" : "Un",
    opts.mailSpeed === "regular" ? "Regular" : "Priority",
    shortCampaignDate(opts.lodgementDate),
  ]
    .filter(Boolean)
    .join(" ");
  if (!opts.taken.includes(base)) return base;
  let n = 2;
  while (opts.taken.includes(`${base} ${n}`)) n += 1;
  return `${base} ${n}`;
}

export function formatNumbers(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "";
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** At most four characters: 4.9k, 86k, 110m. Floors, so 4982 is 4.9k and 86867 is 86k. */
export function compactCount(n: number): string {
  const v = Math.max(0, Math.floor(n));
  if (v < 1_000) return String(v);
  if (v < 10_000) {
    const tenths = Math.floor(v / 100);
    const whole = Math.floor(tenths / 10);
    const frac = tenths % 10;
    return frac === 0 ? `${whole}k` : `${whole}.${frac}k`;
  }
  if (v < 1_000_000) return `${Math.floor(v / 1_000)}k`;
  // ponytail: 999m is the ceiling; a billion would need another suffix.
  return `${Math.min(999, Math.floor(v / 1_000_000))}m`;
}

/** Dollars with two decimals and thousands separators. */
export function formatPrices(dollars: number): string {
  return dollars.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Legacy order ids were zero-padded to four digits. */
export function prefixZeros(n: number | string): string {
  return String(n).padStart(4, "0");
}

export const dateInputValue = (epoch: number | null | undefined): string =>
  epoch ? new Date(epoch * 1000).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);

export const epochFromDateInput = (v: string): number | null =>
  v ? Math.floor(new Date(v).getTime() / 1000) : null;

/** The legacy `"First Last - ID n"` audit strings, split for display. */
export function splitAuditBy(v: string | null | undefined): { name: string; id: string } | null {
  if (!v) return null;
  const [name, id] = v.split(" - ");
  return { name: name ?? v, id: id ?? "" };
}

export function splitName(name: string): { firstName: string; lastName: string } {
  const [firstName = "", ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
}

export const customerTitle = (c: Pick<Customer, "organisation"> | null | undefined): string =>
  c?.organisation ?? "";

export const productSizeLabel = (p: Pick<Product, "width_mm" | "height_mm">): string =>
  `${p.width_mm} x ${p.height_mm}mm`;

export const isStaffRole = (role: string | undefined): boolean => isStaff({ role });

/** One campaign's page or its design tool. The campaign list is not one. */
export const isCampaignPath = (pathname: string): boolean =>
  /^\/app\/[^/]+\/(mycampaigns|design)\/[^/]+/.test(pathname);

/**
 * The DIY design behind the campaign's artwork choice. A campaign design
 * wins; otherwise the design a selected library PDF was rendered from.
 * Null for an uploaded PDF or no choice.
 */
export const selectedDesignId = (
  campaign: Pick<Campaign, "artwork_id" | "design_id">,
  artworks: Pick<Artwork, "id" | "design_id">[],
): string | null =>
  campaign.design_id ??
  (campaign.artwork_id
    ? (artworks.find((a) => a.id === campaign.artwork_id)?.design_id ?? null)
    : null);

export type PageSize = { key: string; label: string; width: number; height: number; pages: number };

/** One design size per trim size and page count, e.g. "210 x 99 (DL) 2pp". */
export function pageSizes(
  products: Pick<Product, "width_mm" | "height_mm" | "pages" | "size">[],
): PageSize[] {
  const sizes = new Map<string, PageSize>();
  for (const p of products) {
    const key = `${p.width_mm}x${p.height_mm}x${p.pages}`;
    if (sizes.has(key)) continue;
    sizes.set(key, {
      key,
      label: `${p.width_mm} x ${p.height_mm}${p.size ? ` (${p.size})` : ""} ${p.pages}pp`,
      width: p.width_mm,
      height: p.height_mm,
      pages: p.pages,
    });
  }
  return [...sizes.values()];
}

/** Rows per page. The first is the default; a stored choice must be one of these. */
export const PAGE_SIZES = [10, 25, 50, 100] as const;

/** A stored page size, or the default when the value is missing or stale. */
export function parsePageSize(raw: string | null): number {
  const n = Number(raw);
  return (PAGE_SIZES as readonly number[]).includes(n) ? n : PAGE_SIZES[0];
}

/**
 * Page buttons for a pager: first, last, current ±1, "…" for gaps. 0-based.
 * Five or fewer pages are listed in full, so the ellipsis only appears once
 * a sixth page exists.
 * Built from those positions, so a long address preview does not walk every slot.
 */
export function pageItems(page: number, count: number): (number | "…")[] {
  if (count <= 5) return Array.from({ length: Math.max(0, count) }, (_, n) => n);
  const raw = Math.floor(page);
  const at = Number.isFinite(raw) ? Math.min(count - 1, Math.max(0, raw)) : 0;
  const wanted = [0, at - 1, at, at + 1, count - 1]
    .filter((n) => n >= 0 && n < count)
    .sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  for (const n of wanted) {
    if (n === out[out.length - 1]) continue;
    const prev = out[out.length - 1];
    if (typeof prev === "number" && n > prev + 1) out.push("…");
    out.push(n);
  }
  return out;
}

export type ArtworkSize = { name: string; width: number; height: number };

/** Trim, or trim plus the usual 3mm bleed each side, in either orientation. */
export function fitsProduct(
  product: Pick<Product, "width_mm" | "height_mm">,
  width: number,
  height: number,
): boolean {
  const near = (a: number, b: number) => [b, b + 6].some((t) => Math.abs(a - t) <= 1);
  return (
    (near(width, product.width_mm) && near(height, product.height_mm)) ||
    (near(width, product.height_mm) && near(height, product.width_mm))
  );
}

/**
 * Trim size for an artwork. PDF MediaBoxes usually include 3mm bleed, so trim
 * or trim + 6mm both match. The returned width/height follow the artwork's
 * orientation, not the product's stored one.
 */
export function artworkSize(
  products: Pick<Product, "width_mm" | "height_mm" | "size">[],
  width: number,
  height: number,
): ArtworkSize {
  const hit = products.find((p) => p.size && fitsProduct(p, width, height));
  if (!hit?.size) {
    const w = Math.round(width);
    const h = Math.round(height);
    return { name: `${w} x ${h}`, width: w, height: h };
  }
  const wide = width >= height;
  return {
    name: hit.size,
    width: Math.round(
      wide ? Math.max(hit.width_mm, hit.height_mm) : Math.min(hit.width_mm, hit.height_mm),
    ),
    height: Math.round(
      wide ? Math.min(hit.width_mm, hit.height_mm) : Math.max(hit.width_mm, hit.height_mm),
    ),
  };
}

/** Saved page mapping: campaign page index -> 1-based artwork page. */
export function artworkPages(c: { artwork_pages?: string | null }): number[] | null {
  if (!c.artwork_pages) return null;
  try {
    const parsed: unknown = JSON.parse(c.artwork_pages);
    if (
      !Array.isArray(parsed) ||
      parsed.length === 0 ||
      parsed.some((n) => typeof n !== "number" || !Number.isInteger(n) || n < 1)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** "A4 (210x297mm)", or "148x210mm" when the size has no product name. */
export function sizeLabel(s: ArtworkSize): string {
  return s.name === `${s.width} x ${s.height}`
    ? `${s.width}x${s.height}mm`
    : `${s.name} (${s.width}x${s.height}mm)`;
}

// ------------------------------------------------------ account dashboard

export interface AccountSummary {
  campaignCount: number;
  orderCount: number;
  spentDollars: number;
  recentCampaigns: Campaign[];
  recentOrders: Order[];
}

/** A cancelled or refunded order is not money the customer spent. */
const SPENT_STATUSES = new Set(["paid", "in_production", "posted"]);

/** Tiles and "recent" lists for the account overview, from data already loaded. */
export function accountSummary(orders: Order[], campaigns: Campaign[], limit = 5): AccountSummary {
  const live = campaigns.filter((c) => c.status !== "deleted");
  const placed = (o: Order) => o.placed_at ?? o.created_at;
  return {
    campaignCount: live.length,
    orderCount: orders.length,
    spentDollars:
      orders.reduce((sum, o) => (SPENT_STATUSES.has(o.status) ? sum + o.total_cents : sum), 0) /
      100,
    recentCampaigns: [...live].sort((a, b) => b.updated_at - a.updated_at).slice(0, limit),
    recentOrders: [...orders].sort((a, b) => placed(b) - placed(a)).slice(0, limit),
  };
}
